import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { Checkbox, FormControl, FormControlLabel, FormGroup, TextField } from '@mui/material';
import CryptoJS from 'crypto-js';

import AuthContainer from '@/components/Container/AuthContainer';
import { InputWrapper } from '@/components/Container/AuthContainer/styles';
import { Button } from '@/components/UI/Button';
import { Text } from '@/components/UI/Typography/Text';
import { LabelWrapper } from '@/components/Account/PreviewApplicationInfo/styled';
import { Box } from '@/layout/Box';
import TermsModal from '@/components/Account/RegisterForm/TermsModal';
import { Flex, LoadingPage } from '@/layout';
import { setBridgePassword } from '@/api/bridge.api';
import { errorHandler, successHandler } from '@/utils/toastHandler';
import useResize from '@/hooks/useResize';
import PATH from '@/router/path';
import useAuthValidation from '@/hooks/requireSchema/useAuthValidation';
import { H1, H2 } from '@/components/UI/Typography/Heading';
import { theme } from '@/theme/Theme';
import useCurrentLang from '@/hooks/useCurrentLang';

interface SetPasswordForm {
  password: string;
  password_confirmation: string;
}

const BridgeSetPassword = () => {
  // @ts-ignore
  const currentLang = useCurrentLang();
  const { t } = useTranslation();
  const { isMobile } = useResize();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  // 從 navigation state 取得 token 和 email，並保存到組件狀態中
  const [credentials, setCredentials] = useState<{ token: string; email: string } | null>(null);

  // 初始化時從 location.state 取得並保存到狀態
  useEffect(() => {
    const { token, email } = (location.state as { token: string; email: string }) || {};
    if (token && email) {
      setCredentials({ token, email });
    } else if (!credentials) {
      // 只在沒有保存的憑證時才導向
      errorHandler(t('error.invalidAccess'));
      navigate(`/${currentLang}`);
    }
  }, []);

  const {
    handleSubmit,
    control,
    watch,
    formState: { isDirty },
  } = useForm<SetPasswordForm>();

  // 密碼確認邏輯
  const passwordRef = useRef('');
  const currentPassword = (passwordRef.current = watch('password', ''));
  const { passwordRegister } = useAuthValidation();

  // 條款checkbox狀態
  const [checkBox, setCheckBox] = useState({
    infoCollect: false,
    terms: false,
    agreement: false,
  });

  const { infoCollect, terms, agreement } = checkBox;
  const error = [infoCollect, terms, agreement].filter((v) => v).length !== 3;

  const handleChange = (value: string, current: boolean) => {
    setCheckBox({ ...checkBox, [value]: !current });
    handleClose(value);
  };

  const [open, setOpen] = useState({
    infoCollect: false,
    terms: false,
  });

  const handleClose = (key: string) => {
    setOpen({ ...open, [key]: false });
  };

  const handleOpen = (key: string) => {
    setOpen({ ...open, [key]: true });
  };

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const onSubmit = handleSubmit(async (data) => {
    if (!credentials) {
      errorHandler(t('error.invalidAccess'));
      return;
    }

    try {
      setIsLoading(true);

      const requestData: setPasswordForm = {
        token: credentials.token,
        email: credentials.email,
        password: CryptoJS.MD5(data.password).toString(),
        password_confirmation: CryptoJS.MD5(data.password_confirmation).toString(),
        tcAgreement: infoCollect,
        PICsAgreement: terms,
      };

      const response = await setBridgePassword(requestData);

      if (response.status === 200 || response.status === 201) {
        successHandler(t('success.passwordReset'));
        navigate(PATH.auth.signIn);
      }
    } catch (error: any) {
      errorHandler(error.response?.data?.message || t('error.server'));
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <>
      <LoadingPage isLoading={isLoading} />
      <AuthContainer title={t('setNewPassword')}>
        <Box position="absolute" top={'-70px'} left={'-30%'}>
          <H2 fontWeight={600} fontSize={theme.fontSizes.lg} textAlign={'center'} style={{ whiteSpace: 'nowrap' }}>
            {t('pages.auth.setNewPassword.welcome')}
          </H2>
        </Box>
        <form onSubmit={onSubmit}>
          <Text marginTop={'15px'}>{t('pages.auth.setNewPassword.illustrate')}</Text>

          <InputWrapper style={{ height: 'auto' }}>
            <Controller
              control={control}
              defaultValue=""
              name={'password'}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <TextField
                    label={t('form.newPassword.label')}
                    placeholder={t('form.newPassword.placeholder')}
                    variant="standard"
                    value={value}
                    sx={{ width: '100%' }}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : t('form.registrationPassword.placeholder')}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="off"
                  />
                </>
              )}
              rules={passwordRegister}
            />
          </InputWrapper>

          <InputWrapper style={{ height: 'auto' }}>
            <Controller
              control={control}
              defaultValue=""
              name={'password_confirmation'}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <TextField
                    label={t('form.reconfirmNewPassword.label')}
                    placeholder={t('form.reconfirmNewPassword.placeholder')}
                    variant="standard"
                    value={value}
                    sx={{ width: '100%' }}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="off"
                  />
                </>
              )}
              rules={{
                required: t('required.reconfirmNewPassword'),
                validate: (value) => value === currentPassword || t('error.reconfirmNewPassword'),
              }}
            />
          </InputWrapper>

          <FormControl sx={{ paddingTop: '15px' }} required error={error} component="fieldset">
            <FormGroup style={{ gap: '15px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{
                      color: '#999999',
                      '&.Mui-checked': {
                        color: '#999999',
                      },
                    }}
                    checked={agreement}
                    onChange={() => {
                      setCheckBox({ ...checkBox, agreement: !checkBox.agreement });
                    }}
                    name="agreement"
                  />
                }
                label={
                  <LabelWrapper>
                    <Flex flexDirection={'column'}>
                      <span>{t('pages.auth.setNewPassword.form.agreement')}</span>
                    </Flex>
                  </LabelWrapper>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{
                      color: '#999999',
                      '&.Mui-checked': {
                        color: '#999999',
                      },
                    }}
                    checked={infoCollect}
                    onChange={() => handleOpen('infoCollect')}
                    name="infoCollect"
                  />
                }
                label={
                  <LabelWrapper>
                    {currentLang === 'en' ? (
                      <Flex flexDirection={'column'}>
                        <span>{t('form.terms.label.pic.prev')}</span>
                        <span className="term">{t('form.terms.label.pic.last')}</span>
                      </Flex>
                    ) : (
                      <Flex>
                        <span>{t('form.terms.label.pic.prev')}</span>
                        <span className="term">{t('form.terms.label.pic.last')}</span>
                      </Flex>
                    )}
                  </LabelWrapper>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{
                      color: '#999999',
                      '&.Mui-checked': {
                        color: '#999999',
                      },
                    }}
                    checked={terms}
                    onChange={() => handleOpen('terms')}
                    name="terms"
                  />
                }
                label={
                  <LabelWrapper>
                    {
                      currentLang === 'en' ? (
                        <Flex flexDirection={'column'}>
                          <span>{t('form.terms.label.t&c.prev')}</span>
                          <span className="term">{t('form.terms.label.t&c.last')}</span>
                        </Flex>
                      ) : (
                        <Flex>  
                          <span>{t('form.terms.label.t&c.prev')}</span>
                          <span className="term">{t('form.terms.label.t&c.last')}</span>
                        </Flex>
                      )
                    }
                  </LabelWrapper>
                }
              />
            </FormGroup>
          </FormControl>

          <Box marginTop={20}>
            <Button
              width={isMobile ? '100%' : 'unset'}
              size={'lg'}
              variant={'primary'}
              rounded={false}
              marginBottom={15}
              disabled={!isDirty || error}
            >
              {t('buttons.submit')}
            </Button>
          </Box>
        </form>

        <TermsModal open={open} handleClose={handleClose} handleChange={handleChange} />
      </AuthContainer>
    </>
  );
};

export default BridgeSetPassword;
